import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiCheckCircle } from 'react-icons/fi';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const Home = () => {
  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-slate-900">
        <motion.div 
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.5 }}
          transition={{ duration: 2.5, ease: "easeOut" }}
          className="absolute inset-0 z-0"
        >
          <img 
            src="https://media-manager-c.questera.ai/greta-media/b3858647681825aecd68df56d8217cb5094538977d896e84f508e759d2eeedf7b0dda9d7934361b5765cf6718803dd25/images/aW1hZ2UvanBlZw==/aed9239f542d9e3f4766c6f18fa0e12e.jpg" 
            alt="Professionelle Baustelle" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent"></div>
        </motion.div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 py-2 px-4 rounded-full bg-red-600/20 border border-red-600/30 text-red-400 text-xs font-bold uppercase tracking-widest mb-8">
              <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></span>
              Meisterbetrieb aus Pleinfeld
            </motion.div>
            
            <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl lg:text-8xl font-black text-white leading-none mb-8 tracking-tighter">
              Kittsteiner <br />
              <span className="text-red-600">Hoch-Tiefbau</span>
            </motion.h1>
            
            <motion.h2 variants={itemVariants} className="text-xl md:text-2xl text-slate-300 font-light mb-12 leading-relaxed max-w-2xl">
              Ihr Partner für massive Bauqualität – von der ersten <span className="text-white font-semibold">Planung</span> bis zum <span className="text-white font-semibold">fertigen Gewerk</span>.
            </motion.h2>
            
            <motion.div variants={itemVariants} className="flex flex-wrap gap-6">
              <Link 
                to="/kontakt" 
                className="inline-flex items-center justify-center bg-red-700 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-red-800 transition-all group shadow-2xl hover:shadow-red-700/40"
              >
                Projekt anfragen
                <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-2 transition-transform" />
              </Link>
              <Link 
                to="/leistungen" 
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-md text-white border border-white/20 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all"
              >
                Unsere Leistungen
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-32 bg-white overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-20">
            <motion.div 
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="lg:w-1/2 relative"
            >
              <div className="relative z-10 rounded-[2.5rem] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.15)]">
                <motion.img 
                  whileHover={{ scale: 1.08 }}
                  transition={{ duration: 1 }}
                  src="https://media-manager-c.questera.ai/greta-media/b3858647681825aecd68df56d8217cb5094538977d896e84f508e759d2eeedf7b0dda9d7934361b5765cf6718803dd25/images/aW1hZ2UvanBlZw==/a0be7b8747449ed3dafbcd8877abaa78.jpg" 
                  alt="Baustelle im Hoch- und Tiefbau" 
                  className="w-full h-[450px] md:h-[600px] object-cover"
                />
              </div>
              
              {/* Region Badge mit Floating & Mobile Versatz */}
              <motion.div 
                animate={{ y: [0, -20, 0] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                className="absolute bottom-4 right-4 md:-bottom-10 md:-right-10 bg-red-700 text-white p-8 md:p-12 rounded-[2rem] shadow-2xl z-20 border-[6px] border-white"
              >
                <p className="text-4xl md:text-6xl font-black leading-none tracking-tighter">Region</p>
                <p className="text-xs md:text-base uppercase tracking-[0.3em] font-bold opacity-80 mt-3">Pleinfeld & Umgebung</p>
              </motion.div>
              
              <div className="absolute -top-12 -left-12 w-64 h-64 bg-red-50 rounded-full -z-0 blur-3xl opacity-60"></div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="lg:w-1/2"
            >
              <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-10 leading-tight">
                Bauen ist <br />
                <span className="text-red-700 relative">
                  Vertrauenssache
                  <motion.span 
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="absolute -bottom-2 left-0 h-1.5 bg-red-700/20 rounded-full"
                  ></motion.span>
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12 leading-relaxed">
                Als lokaler Meisterbetrieb setzen wir auf direkte Kommunikation und handwerkliche Präzision. Wir realisieren Ihre Träume im Hoch- und Tiefbau mit moderner Technik und langjähriger Erfahrung.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  "Präzise Ausführung",
                  "Regionale Verbundenheit",
                  "Transparente Preise",
                  "Moderne Maschinen"
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + (i * 0.1) }}
                    className="flex items-center gap-5 text-slate-900 font-bold bg-slate-50 p-6 rounded-2xl border border-slate-100 hover:border-red-200 hover:bg-white hover:shadow-lg transition-all cursor-default"
                  >
                    <div className="w-10 h-10 rounded-xl bg-red-700 text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-red-700/20">
                      <SafeIcon icon={FiCheckCircle} className="text-xl" />
                    </div>
                    {item}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;